import React from 'react';
import { Modal, ModalBody, Button, Badge, UncontrolledAlert } from 'reactstrap';
import { FaChartBar, FaTimes, FaArrowRight } from 'react-icons/fa';
import usePromoPrompt, { PromoSurface } from '../../hooks/usePromoPrompt';
import OutboundLink from '../common/OutboundLink';

const PromoChip = ({ label }: { label: string }) =>
  label ? (
    <Badge className="dp-yellow-chip" pill>
      <FaChartBar aria-hidden="true" />
      {label}
    </Badge>
  ) : null;

/**
 * DentalPost promo prompt (DP-2225): a top-nav banner and/or a first-visit
 * pop-up, driven by the build-time HubDB promo. Both surfaces are optional per
 * campaign; this renders whichever the current promo opted into.
 *
 * `mode` scopes which surface this instance renders so the banner can live
 * inside each layout while the pop-up stays mounted once at the Layout root
 * (see usePromoPrompt). Defaults to rendering both.
 */
const PromoPrompt = ({ mode = 'both' }: { mode?: PromoSurface }) => {
  const { promo, bannerOpen, popupOpen, closePopup } = usePromoPrompt(mode);

  // The promo is a marketing interstitial that overlays the app. Suppress it
  // under Cypress so its banner/pop-up never intercepts e2e interactions.
  // `window.Cypress` is injected by the test runner and is absent in real
  // browsers, so this is a no-op in production.
  const isCypress =
    typeof window !== 'undefined' &&
    !!(window as { Cypress?: unknown }).Cypress;

  if (!promo || isCypress) return null;

  return (
    <>
      {mode !== 'popup' && bannerOpen && (
        <UncontrolledAlert
          className="dp-promo-banner border-0 rounded-0"
          role="region"
          aria-label={promo.chipLabel || 'Promotion'}
          fade={false}
        >
          <div>
            <PromoChip label={promo.chipLabel} />
            <p className="dp-promo-banner__heading mb-0">
              {promo.bannerHeading}
            </p>
          </div>
          <Button tag={OutboundLink} color="primary" href={promo.bannerCtaUrl}>
            {promo.ctaLabel || 'Learn more'}
            <FaArrowRight aria-hidden="true" />
          </Button>
        </UncontrolledAlert>
      )}

      {mode !== 'banner' && (
        <Modal
          isOpen={popupOpen}
          toggle={closePopup}
          centered
          contentClassName="dp-promo-modal"
        >
          <button
            type="button"
            className="dp-promo-modal__close"
            onClick={closePopup}
            aria-label="Close"
          >
            <FaTimes aria-hidden="true" />
          </button>
          <div className="dp-promo-modal__head">
            <PromoChip label={promo.chipLabel} />
            <h2 className="dp-promo-modal__title">{promo.modalHeading}</h2>
          </div>
          <ModalBody className="dp-promo-modal__body">
            {promo.modalBody && (
              <p className="dp-promo-modal__desc">{promo.modalBody}</p>
            )}
            {promo.bullets.length > 0 && (
              <ul className="dp-promo-checklist">
                {promo.bullets.map((bullet, i) => (
                  <li key={i}>
                    <span className="dp-promo-dot" aria-hidden="true" />
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
            <div className="dp-promo-modal__actions">
              <Button
                tag={OutboundLink}
                color="primary"
                href={promo.popupCtaUrl}
                onClick={closePopup}
              >
                {promo.ctaLabel || 'Learn more'}
                <FaArrowRight aria-hidden="true" />
              </Button>
              {promo.secondaryLabel && (
                <Button color="secondary" onClick={closePopup}>
                  {promo.secondaryLabel}
                </Button>
              )}
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default PromoPrompt;
